import React, { useState, useEffect } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NavBar from '@/components/NavBar';
import { Button } from '@/components/ui/button';
import { Photo } from '@/components/PhotoGrid';
import QRCodeModal from '@/components/QRCodeModal';
import LanguageSwitcher from '@/components/LanguageSwitcher';

// Image host base URL (new host stores absolute URLs in config)
const API_BASE_URL = 'https://cdn-bun.stardots.io/swainphoto';
const resolvePhotoUrl = (path: string) => path.startsWith('http') ? path : `${API_BASE_URL}/${path}`;

const Index = () => {
  const { t } = useTranslation();
  const [bannerPhotos, setBannerPhotos] = useState<Photo[]>([]);
  const [featuredPhotos, setFeaturedPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    qrCodeUrl: string;
    title: string;
  }>({
    isOpen: false,
    qrCodeUrl: '',
    title: ''
  });
  
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const response = await fetch(`/homepage.json`);
        const data = await response.json();
        
        // Transform data to match Photo interface
        const transformPhoto = (photo: any): Photo => ({
          id: photo.id,
          title: photo.title,
          description: photo.description,
          url: resolvePhotoUrl(photo.full_url),
          thumbnailUrl: resolvePhotoUrl(photo.preview_url),
          width: photo.width,
          height: photo.height,
          category: photo.category !== 'uncategorized' ? photo.category : undefined,
          createdAt: photo.date || new Date().toISOString()
        });
        
        setBannerPhotos(data.banner.map(transformPhoto));
        setFeaturedPhotos(data.featured.map(transformPhoto));
      } catch (error) {
        console.error('Error fetching homepage data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchHomeData();
    
    // Auto-rotate banner images every 5 seconds
    const interval = setInterval(() => {
      setCurrentBannerIndex(prevIndex => {
        return bannerPhotos.length > 0 ? (prevIndex + 1) % bannerPhotos.length : 0;
      });
    }, 5000);
    
    return () => clearInterval(interval);
  }, [bannerPhotos.length]);
  
  const goToPrevBanner = () => {
    setCurrentBannerIndex(prevIndex => {
      return prevIndex === 0 ? bannerPhotos.length - 1 : prevIndex - 1;
    });
  };
  
  const goToNextBanner = () => {
    setCurrentBannerIndex(prevIndex => {
      return (prevIndex + 1) % bannerPhotos.length;
    });
  };
  
  const openQRCodeModal = (qrCodeUrl: string, title: string) => {
    setModalState({
      isOpen: true,
      qrCodeUrl,
      title
    });
  };

  const closeQRCodeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  return (
    <div className="min-h-screen">
      <NavBar transparent />
      
      {/* Hero Section */}
      <section className="h-screen flex items-center justify-center relative overflow-hidden">
        {/* Banner Carousel */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 w-full z-0">
          <div className="relative w-full h-[80vh] flex items-center justify-center">
            {bannerPhotos.length > 0 && (
              bannerPhotos.map((photo, index) => (
                <div
                  key={photo.id}
                  className={`absolute inset-0 transition-opacity duration-1000 flex items-center justify-center ${index === currentBannerIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                >
                  <img
                    src={photo.url}
                    alt={photo.title || 'Banner image'}
                    className="max-w-full max-h-[80vh] w-auto h-auto rounded-md shadow-lg"
                  />
                </div>
              ))
            )}
          </div>
          
          {/* Banner navigation arrows */}
          {bannerPhotos.length > 1 && (
            <>
              <button 
                onClick={goToPrevBanner}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full transition-colors"
                aria-label="Previous banner"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button 
                onClick={goToNextBanner}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full transition-colors"
                aria-label="Next banner"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {/* Content 层 z-20 */}
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center text-white px-4 pointer-events-none">
            <h1 
              className="max-w-lg mx-auto text-base md:text-2xl opacity-90 mb-6 animate-fade-in font-dancing-script cursor-pointer transition-all duration-700 hover:opacity-100 hover:-translate-y-0.5 hover:drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]" 
              style={{ animationDelay: '200ms' }}
            >
              {t('home.hero.title')}
            </h1>
            <div className="pointer-events-auto">
              <Button
                asChild
                className="animate-fade-in rounded-none border-white text-white bg-transparent hover:bg-white hover:text-black transition-all duration-300"
                style={{ animationDelay: '400ms' }}
              >
                <Link to="/gallery">
                  {t('common.explore')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Featured Works */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-playfair mb-12 text-center">{t('home.featured.title')}</h2>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(index => (
                <div key={index} className="h-80 bg-gray-200 animate-pulse rounded-md" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredPhotos.map((photo, index) => (
                <Link 
                  key={photo.id} 
                  to={`/photo/${photo.id}`}
                  className="group relative h-80 overflow-hidden rounded-md"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <img 
                    src={photo.thumbnailUrl}
                    alt={photo.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 rounded-md"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                    <h3 className="text-white text-xl font-playfair">{photo.title}</h3>
                    {photo.category && photo.category !== 'uncategorized' && (
                      <span className="text-white/70 text-sm mt-1">{photo.category}</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
          
          <div className="flex justify-center mt-12">
            <Button
              asChild
              variant="outline"
              className="rounded-none text-sm uppercase tracking-wider hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
            >
              <Link to="/gallery">
                {t('common.viewAll')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
      
      {/* About Section */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl font-playfair mb-8">{t('home.about.title')}</h2>
          <p className="text-lg mb-8">
            {t('home.about.content')}
          </p>
          <p className="italic text-muted-foreground">
            {t('home.about.quote')}
          </p>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-12 bg-photo-dark text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <Link to="/" className="text-2xl font-playfair tracking-wider mb-4 md:mb-0">
              Swain Photography
            </Link>
            
            <nav className="flex items-center space-x-8">
              <Link to="/" className="text-sm uppercase tracking-wider hover:text-photo-accent transition-colors">
                {t('home.footer.portfolio')}
              </Link>
              <Link to="/gallery" className="text-sm uppercase tracking-wider hover:text-photo-accent transition-colors">
                {t('home.footer.gallery')}
              </Link>
            </nav>
          </div>

          {/* Social Media Links */}
          <div className="flex justify-center space-x-6 mt-8">
            <button 
              onClick={() => openQRCodeModal('/imgs/douyin-qr.jpg', t('social.douyin'))}
              className="hover:opacity-80 transition-opacity"
              aria-label={t('social.douyin')}
            >
              <img src="/imgs/douyin.svg" alt={t('social.douyin')} className="w-8 h-8" />
            </button>
            <button 
              onClick={() => openQRCodeModal('/imgs/weibo-qr.jpg', t('social.weibo'))}
              className="hover:opacity-80 transition-opacity"
              aria-label={t('social.weibo')}
            >
              <img src="/imgs/weibo.svg" alt={t('social.weibo')} className="w-8 h-8" />
            </button>
            <button 
              onClick={() => openQRCodeModal('/imgs/rednote-qr.jpg', t('social.rednote'))}
              className="hover:opacity-80 transition-opacity"
              aria-label={t('social.rednote')}
            >
              <img src="/imgs/rednote.svg" alt={t('social.rednote')} className="w-8 h-8" />
            </button>
            <button 
              onClick={() => openQRCodeModal('/imgs/wechat-qr.jpg', t('social.wechat'))}
              className="hover:opacity-80 transition-opacity"
              aria-label={t('social.wechat')}
            >
              <img src="/imgs/wechat.svg" alt={t('social.wechat')} className="w-8 h-8" />
            </button>
          </div>
          
          <div className="border-t border-white/20 mt-8 pt-8 text-center md:text-left text-sm text-white/60">
            {t('home.footer.copyright', { year: new Date().getFullYear() })}
          </div>
        </div>
      </footer>

      <QRCodeModal
        isOpen={modalState.isOpen}
        onClose={closeQRCodeModal}
        qrCodeUrl={modalState.qrCodeUrl}
        title={modalState.title}
      />
    </div>
  );
};

export default Index;
